import moment from 'moment';
import { Component } from 'react';
import type { ChangeEvent } from 'react';
import { Modal } from 'react-bootstrap';
import _ from 'underscore';
import { ErrorAlert } from '../../generic/ErrorAlert';
import { Action } from '../../../../typings/permissions';
import RevisionActions from '../../../actions/RevisionActions';
import { window } from '../../../globals';
import { userHasPermissionForProject } from '../../../helpers/permissions';
import { remove } from '../../../redux/api';
import { getAddAnotherText, nuc } from '../../../utils';
import Button, { ButtonGroup, IconButton, BUTTON_TYPE, TooltipButton } from '../../generic/Button';
import Icon, { icons } from '../../generic/Icon';
import Loader from '../../generic/Loader';
import LicenseEditableList from '../../licenses/EditableList';
import { getLicenseGroupsFromRevision } from '../../licenses/groups';
import type { RevisionDependency, RevisionLicense } from '../../../types';
import type { Change } from '../../licenses/EditableList';
import type { LicenseGroups } from '../../licenses/groups';
import { WithDialogContext } from '../../../contexts/DialogContext';
import type { DialogState } from '../../../contexts/DialogContext';

//  Takes any object which has a `licenses` array and returns just that.
const getLicenses = ({ licenses }: { licenses: RevisionLicense[] }) => licenses;

export type OnSaveDependencyState = Partial<Omit<ModalEditDependencyState, 'licenseGroups'>> & {
  licenses: RevisionLicense[][];
};

type Props = {
  revision?: RevisionDependency;
  onHide(): void;
  onDependencySave: (locator: string, state: OnSaveDependencyState) => Promise<any>;
  show?: boolean;
  // send parent revision to backend to trigger an IssueScan
  parentRevisionLocator?: string;
  //  If there are errors fetching the revision in the parent component, we can pass them here.
  fetchRevisionErrors?: string[] | null;
  dialogContext: DialogState;
};

export type ModalEditDependencyState = {
  loading: boolean;
  error?: Error;
  rebuildingDependency: boolean;
  title: string;
  url: string;
  description: string;
  licenseGroups: LicenseGroups;
  authors: string[];
};

class ModalEditDependency extends Component<Props, ModalEditDependencyState> {
  constructor(props: Props) {
    super(props);
    this.state = this.getInitialStateObj(props);
  }

  getInitialStateObj(props: Props): ModalEditDependencyState {
    const proj = props.revision && props.revision.project ? props.revision.project : {};
    return {
      loading: false,
      rebuildingDependency: false,
      title: proj.title,
      url: proj.url,
      description: proj.description,
      licenseGroups: props.revision ? getLicenseGroupsFromRevision(props.revision) : [],
      authors: proj.authors || [],
    };
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (this.props.show !== nextProps.show) {
      this.resetDep(nextProps);
    }
  }

  resetDep = (props) => this.setState(this.getInitialStateObj(props));

  //  Determine if the revision has a previously saved projectCorrection.
  get hasCorrection(): boolean {
    const { revision } = this.props;
    return (revision && (revision?.corrected || revision?.project?.projectCorrection)) ?? false;
  }

  //  Determines if there are any locally unsaved changes. Required for saving (so that we don't save an empty correction).
  get hasUnsavedCorrection(): boolean {
    const { project } = this.props.revision || {};
    //  Don't allow saving if we don't have a project.
    if (!project) {
      return false;
    }
    //  We have an unsaved correction if our prior state doesn't match our changed state.
    return !_.isEqual(this.priorState, this.changedState);
  }

  //  Returns the state of the revision when the component mounted/resetDep was called.
  get priorState() {
    const { project } = this.props.revision || {};
    if (!project) {
      return null;
    }
    const { revision } = this.props;
    const { url, title, description, authors } = project;
    return {
      url,
      title,
      description,
      authors,
      licenses: getLicenseGroupsFromRevision(revision).map(getLicenses),
    };
  }

  //  Determines the state that has been changed since this component mounted/resetDep was called.
  get changedState() {
    const { title, url, description, authors } = this.state;
    return {
      url,
      title,
      description,
      authors,
      licenses: this.flattenedLicenseGroups,
    };
  }

  //  Returns a multi-dimentional array of licenses, from state.licenseGroups.
  get flattenedLicenseGroups(): Array<Array<Partial<RevisionLicense>>> {
    const { licenseGroups } = this.state;
    return licenseGroups.map(getLicenses);
  }

  onEditTextChange = (key: keyof ModalEditDependencyState, event: ChangeEvent<HTMLInputElement>) =>
    this.setState({
      [key]: event.target.value,
    });

  onSave = async (shouldHide = true) => {
    const { revision, onHide, onDependencySave } = this.props;
    this.setState({ loading: true, error: undefined });

    //  Check if anything has actually changed, early return if true.
    if (!this.hasUnsavedCorrection) {
      if (shouldHide) {
        this.resetDep(this.props);
        return onHide();
      }
      return;
    }

    try {
      await onDependencySave(revision.locator, {
        ...this.changedState,
        parentRevisionLocator: this.props.parentRevisionLocator,
      });

      if (shouldHide) {
        this.resetDep(this.props);
        return onHide();
      }
      this.setState({
        loading: false,
      });
    } catch (error) {
      this.setState({
        error,
        loading: false,
      });
    }
  };

  onRevert = async () => {
    // Send parentRevisionLocator to backend when it is available
    // This would trigger an IssueScan
    const deleteQuery = this.props.parentRevisionLocator
      ? `?parentRevisionLocator=${nuc(this.props.parentRevisionLocator)}`
      : '';
    this.props.dialogContext.onConfirm({
      title: 'Delete Project Correction',
      body: (
        <div>
          <p>This will undo any changes you might have made to this component's licenses or metadata</p>
        </div>
      ),
      onConfirm: () =>
        remove(`/api/projects/${nuc(this.props.revision.projectId)}/correction${deleteQuery}`)
          .then(() => window.location.reload())
          .catch(() =>
            this.setState({
              error: new Error('Failed to revert changes'),
            }),
          ),
    });
  };

  onLicenseGroupsChange = (licenseGroups: LicenseGroups, change: Change) => {
    this.setState({ licenseGroups }, () => {
      if (change === 'split') this.onSave(false);
    });
  };

  render() {
    const canEdit = userHasPermissionForProject(Action.Edit, this.props.revision?.projectId);
    const canRebuild = this.props.revision?.project?.organizationId ? canEdit : true;

    return (
      <Modal
        backdrop="static"
        animation
        show={this.props.show}
        onHide={this.props.onHide}
        onEscapeKeyDown={this.props.onHide}
        className="modal-edit-dependency"
      >
        <Modal.Header closeButton>
          <Modal.Title
            style={{
              display: 'flex',
              alignItems: 'center',
              marginRight: '28px',
              justifyContent: 'space-between',
            }}
          >
            Edit Dependency: {this.state.title}
            {this.props.revision && (
              <TooltipButton
                id="reanalyze-button-tooltip"
                // If the value is null, we want to hide the timestamp.
                showTooltip={!!this.props.revision.dependency_cache_updated}
                // Show the exact timestamp to super users for debugging purposes, since it's not shown anywhere else in the UI.
                tooltip={`Last analyzed: ${
                  window?.user?.super
                    ? this.props.revision.dependency_cache_updated
                    : moment(this.props.revision.dependency_cache_updated).fromNow()
                }`}
                type={BUTTON_TYPE.SECONDARY}
                size="large"
                isDisabled={this.state.rebuildingDependency || !canRebuild}
                onClick={() => {
                  RevisionActions.rebuild(this.props.revision.locator);
                  this.setState({ rebuildingDependency: true });
                }}
              >
                {this.state.rebuildingDependency ? <Icon icon={icons.circleO} hasSpin /> : <Icon icon={icons.reload} />}{' '}
                Reanalyze
              </TooltipButton>
            )}
          </Modal.Title>
        </Modal.Header>
        {this.props.fetchRevisionErrors?.length ? (
          <Modal.Body>
            <ErrorAlert status={{ errors: this.props.fetchRevisionErrors }} />
          </Modal.Body>
        ) : (
          // eslint-disable-next-line react/jsx-no-useless-fragment -- Doing this to avoid an ugly nested ternary.
          <>
            {this.state.loading || !this.props.revision ? (
              <Loader />
            ) : (
              <Modal.Body>
                {this.state.error && <ErrorAlert status={{ errors: [this.state.error] }} />}
                <div key={`dep-item-${this.props.revision.locator}`} className="form-fossa">
                  <div className="form-group">
                    <div className="col-md-5">
                      <label>Title</label>
                      <input
                        type="text"
                        className="form-control"
                        value={this.state.title}
                        onChange={(ev) => this.onEditTextChange('title', ev)}
                        disabled={!canEdit}
                      />
                    </div>
                    <div className="col-md-7">
                      <label>Homepage</label>
                      <input
                        type="text"
                        className="form-control"
                        value={this.state.url}
                        onChange={(ev) => this.onEditTextChange('url', ev)}
                        disabled={!canEdit}
                      />
                    </div>
                    <div className="col-md-12 margin-top-lg">
                      <div>
                        <label>Description</label>
                        <textarea
                          className="input-block-level"
                          value={this.state.description}
                          onChange={(ev) => this.onEditTextChange('description', ev)}
                          disabled={!canEdit}
                        />
                      </div>
                    </div>
                  </div>

                  <LicenseEditableList
                    defaultLicenseGroups={this.state.licenseGroups}
                    onChange={this.onLicenseGroupsChange}
                    revision={this.props.revision}
                    canEdit={canEdit}
                    shouldSkipWarning
                  />

                  <div className="form-group">
                    <div className="col-md-12 margin-top-lg">
                      <label>Authors</label>
                      <ul className="list-clean">
                        {_.map(this.state.authors, (author, i) => (
                          <li key={`${this.props.revision.locator}-authors-${i}`}>
                            <input
                              type="email"
                              value={author}
                              onChange={(e) => {
                                const currAuthors = this.state.authors.slice();
                                currAuthors[i] = e.target.value;
                                this.setState({ authors: currAuthors });
                              }}
                              disabled={!canEdit}
                            />
                            <IconButton
                              type={BUTTON_TYPE.ERROR}
                              icon={icons.trash}
                              onClick={() => {
                                const newAuthors = this.state.authors.slice();
                                newAuthors.splice(i, 1);
                                this.setState({ authors: newAuthors });
                              }}
                              isDisabled={!canEdit}
                            />
                          </li>
                        ))}
                      </ul>
                      <Button
                        type={BUTTON_TYPE.SECONDARY}
                        size="small"
                        icon={icons.plusSquareO}
                        isDisabled={!canEdit}
                        onClick={() => {
                          const newAuthors = this.state.authors.slice();
                          newAuthors.push('');
                          this.setState({ authors: newAuthors });
                        }}
                      >
                        {getAddAnotherText('author', this.state.authors.length)}
                      </Button>
                    </div>
                  </div>
                </div>
              </Modal.Body>
            )}
          </>
        )}
        <Modal.Footer>
          <ButtonGroup>
            <Button type={BUTTON_TYPE.SECONDARY} onClick={this.props.onHide}>
              Cancel
            </Button>
            {canEdit && this.hasCorrection && (
              <Button type={BUTTON_TYPE.ERROR} onClick={this.onRevert} isDisabled={!canEdit}>
                Delete Correction
              </Button>
            )}
            {canEdit && (
              <Button type={BUTTON_TYPE.PRIMARY} onClick={this.onSave} isDisabled={!this.hasUnsavedCorrection}>
                <Icon icon={icons.save} /> Save Changes
              </Button>
            )}
          </ButtonGroup>
        </Modal.Footer>
      </Modal>
    );
  }
}

export default WithDialogContext(ModalEditDependency);
